import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { BadgeCheck, CheckCircle2, Loader2, PencilRuler, ShieldCheck, Upload, X } from "lucide-react";
import Layout from "@/components/layout/Layout";
import { Crumbs } from "@/components/layout/Crumbs";
import { Fold, FoldBody } from "@/components/layout/Fold";
import { LandingHead } from "@/components/shop/GroupCard";
import api, { formatApiError } from "@/lib/api";
import { useMall } from "@/context/MallContext";
import { useAuth } from "@/context/AuthContext";
import { themeOf, tint } from "@/lib/mall";
import { StoreButton } from "@/components/Buttons";
import { JobCards } from "@/components/artwork/JobCards";
import { Lessons } from "@/components/artwork/Lessons";

const input = "w-full border-2 border-zinc-900 bg-white px-4 py-3 text-base text-zinc-900 placeholder:text-zinc-500 focus:outline-none focus:ring-2 focus:ring-zinc-900/30";
const VERDICT = { best: "text-[#1e6b34]", good: "text-blue-700", ok: "text-amber-700", careful: "text-red-700" };

/** ARTWORK (Phase 10): pick a service and send the files — first thing on the page. The lessons wait in folds below. */
export default function ArtworkRequest() {
    const { shown: shop } = useMall();
    const { user } = useAuth();
    const theme = themeOf(shop);
    const fileRef = useRef();
    const placeRef = useRef();

    const [cfg, setCfg] = useState(null);
    const [form, setForm] = useState({ name: "", email: "", phone: "", description: "", date_needed: "" });
    const [products, setProducts] = useState([]);
    const [hasArt, setHasArt] = useState(null);
    const [service, setService] = useState("");
    const [files, setFiles] = useState([]);
    const [placement, setPlacement] = useState([]);
    const [uploading, setUploading] = useState("");
    const [busy, setBusy] = useState(false);
    const [done, setDone] = useState(false);

    useEffect(() => { api.get("/artwork/config").then(({ data }) => setCfg(data)).catch(() => setCfg(null)); }, []);
    useEffect(() => { if (user) setForm((f) => ({ ...f, name: f.name || user.name || "", email: f.email || user.email || "" })); }, [user]);

    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
    const pick = (name) => setProducts((p) => (p.includes(name) ? p.filter((x) => x !== name) : [...p, name]));

    const upload = async (list, kind) => {
        setUploading(kind);
        try {
            const out = [];
            for (const f of list) {
                const fd = new FormData();
                fd.append("file", f);
                const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
                out.push(data);
            }
            (kind === "art" ? setFiles : setPlacement)((p) => [...p, ...out]);
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
        finally { setUploading(""); }
    };

    const submit = async (e) => {
        e?.preventDefault();
        if (!products.length) { toast.error("Pick what you're making"); return; }
        if (hasArt === null) { toast.error("Do you already have artwork?"); return; }
        if (!service) { toast.error("Pick what you need — check, cleanup or rebuild"); return; }
        if (!form.name || !form.email || !form.description) { toast.error("Name, email and a description please"); return; }
        setBusy(true);
        try {
            const tier = (cfg?.tiers || []).find((t) => t.key === service);
            await api.post("/artwork-requests", {
                ...form, files, placement, products, has_artwork: hasArt, service,
                service_label: tier ? `${tier.name} (${tier.price})` : service,
                store_slug: shop?.slug || null,
            });
            setDone(true);
            window.scrollTo({ top: 0, behavior: "smooth" });
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
        finally { setBusy(false); }
    };

    if (done) return (
        <Layout title="Artwork">
            <div className="mx-auto max-w-xl px-4 py-24 text-center">
                <CheckCircle2 size={48} className="mx-auto" style={{ color: theme.primary }} />
                <h1 className="mt-5 font-display text-3xl font-black uppercase tracking-tight">The art room has it.</h1>
                <p className="mt-3 text-zinc-700" data-testid="artwork-goes-to">
                    {shop?.name || "13th Ave"} got your files and your job list. You'll get a receipt by email,
                    then a real answer from the department — pass, fail, or here's the quote.
                </p>
                <Link to="/" className="mt-7 inline-block"><StoreButton theme={theme}>Back to the shop</StoreButton></Link>
            </div>
        </Layout>
    );

    const jobs = cfg?.jobs || [];
    const chip = (on) => (on
        ? { borderColor: theme.primary, color: theme.on || "#fff", background: theme.primary }
        : { borderColor: "#0b0b0b", color: "#0b0b0b", background: "#fff" });

    return (
        <Layout title="Artwork">
            <section className="mx-auto max-w-7xl px-4 py-8 sm:px-6" data-testid="artwork-page" id="request">
                <Crumbs trail={[{ label: "Artwork" }]} />
                <div className="mt-4">
                    <LandingHead kicker={<span data-testid="artwork-destination">Artwork department · goes to {shop?.name || "13th Ave"}</span>} testid="artwork-head"
                        title="Send us your art. We make it print-ready."
                        line="Not a logo contest, not a design agency — a print department that sets your file up for the exact job so it comes off the machine clean. Pick a service, upload, describe the job." />
                </div>

                <form onSubmit={submit} className="mt-6 space-y-6 border-2 border-zinc-900 bg-white p-5 sm:p-7" data-testid="artwork-request-form">
                    <div>
                        <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-500">What do you need? *</p>
                        <div className="mt-3 grid gap-3 lg:grid-cols-3" data-testid="artwork-tiers">
                            {(cfg?.tiers || []).map((t) => {
                                const on = service === t.key;
                                return (
                                    <button key={t.key} type="button" onClick={() => setService(t.key)} data-testid={`artwork-tier-${t.key}`} aria-pressed={on}
                                        className="flex h-full flex-col border-2 p-4 text-left transition-colors"
                                        style={{ borderColor: on ? theme.primary : "#0b0b0b", background: on ? tint(theme.primary, 0.08) : "#fff" }}>
                                        <p className="flex items-baseline justify-between gap-3">
                                            <span className="font-mono text-[11px] uppercase tracking-[0.24em] text-zinc-500">{t.name}</span>
                                            <span className="font-display text-2xl font-black tracking-tight" style={{ color: on ? theme.primary : "#0b0b0b" }}>{t.price}</span>
                                        </p>
                                        <p className="mt-2 text-sm text-zinc-700">{t.blurb}</p>
                                        <ul className="mt-2 flex-1 space-y-1">
                                            {t.includes.map((i) => (
                                                <li key={i} className="flex gap-2 text-sm text-zinc-700"><BadgeCheck size={15} className="mt-0.5 shrink-0" style={{ color: theme.primary }} /> {i}</li>
                                            ))}
                                        </ul>
                                        <span className="mt-3 inline-block border-2 px-3 py-1.5 text-center font-mono text-[11px] uppercase tracking-[0.18em]" style={chip(on)}>
                                            {on ? "selected" : "choose this"}
                                        </span>
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    <div>
                        <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-500">What are you making? *</p>
                        <div className="mt-3 flex flex-wrap gap-2" data-testid="artwork-product-picker">
                            {jobs.map((j) => (
                                <button key={j.key} type="button" onClick={() => pick(j.name)} data-testid={`artwork-product-${j.key}`} aria-pressed={products.includes(j.name)}
                                    className="border-2 px-4 py-2 text-sm font-semibold transition-colors" style={chip(products.includes(j.name))}>
                                    {j.name}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="grid gap-5 sm:grid-cols-2">
                        <div>
                            <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-500">Do you already have artwork? *</p>
                            <div className="mt-3 flex gap-2">
                                {[["Yes", true], ["No", false]].map(([label, val]) => (
                                    <button key={label} type="button" onClick={() => setHasArt(val)} data-testid={`artwork-has-${label.toLowerCase()}`} aria-pressed={hasArt === val}
                                        className="flex-1 border-2 px-4 py-3 text-sm font-bold transition-colors" style={chip(hasArt === val)}>
                                        {label}
                                    </button>
                                ))}
                            </div>
                        </div>
                        <label className="block">
                            <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-zinc-500">Deadline</p>
                            <input type="date" className={`${input} mt-3`} value={form.date_needed} onChange={set("date_needed")} data-testid="art-date" />
                        </label>
                    </div>

                    {hasArt !== false && (
                        <div>
                            <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading === "art"} data-testid="art-upload"
                                className="flex w-full flex-col items-center gap-2 border-2 border-dashed border-zinc-900 py-7 transition-colors hover:bg-zinc-50">
                                {uploading === "art" ? <Loader2 className="animate-spin" style={{ color: theme.primary }} /> : <Upload style={{ color: theme.primary }} />}
                                <span className="text-sm font-semibold">Upload your artwork — AI, EPS, PDF, PSD, PNG, JPG</span>
                                <span className="font-mono text-[11px] uppercase tracking-[0.16em] text-zinc-500">as many files as you've got</span>
                            </button>
                            <input ref={fileRef} type="file" multiple className="hidden" accept="image/*,.pdf,.ai,.eps,.psd,.svg"
                                onChange={(e) => upload(Array.from(e.target.files), "art")} />
                            {!!files.length && (
                                <div className="mt-3 space-y-2" data-testid="art-files">
                                    {files.map((f, i) => (
                                        <div key={i} className="flex items-center justify-between border border-zinc-300 px-3 py-2 text-sm">
                                            <span className="truncate">{f.filename}</span>
                                            <button type="button" onClick={() => setFiles(files.filter((_, x) => x !== i))} className="text-zinc-500 hover:text-red-600"><X size={15} /></button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    <textarea className={input} rows={5} data-testid="art-description" value={form.description} onChange={set("description")}
                        placeholder="Describe the job — size, colors, the text word for word, where it goes on the shirt or sign, and when you need it." />

                    <div>
                        <button type="button" onClick={() => placeRef.current?.click()} disabled={uploading === "place"} data-testid="art-placement-upload"
                            className="inline-flex items-center gap-2 border-2 border-zinc-900 px-4 py-2 font-mono text-[11px] uppercase tracking-[0.16em] text-zinc-900 hover:bg-ink hover:text-[#fff]">
                            {uploading === "place" ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />} optional: photo of the shirt or sign for placement
                        </button>
                        <input ref={placeRef} type="file" multiple className="hidden" accept="image/*" onChange={(e) => upload(Array.from(e.target.files), "place")} />
                        {!!placement.length && (
                            <p className="mt-2 font-mono text-[11px] uppercase tracking-[0.16em]" style={{ color: theme.primary }} data-testid="art-placement-files">
                                {placement.length} placement photo{placement.length > 1 ? "s" : ""} attached
                            </p>
                        )}
                    </div>

                    <div className="grid gap-3 sm:grid-cols-3">
                        <input className={input} placeholder="Name *" value={form.name} onChange={set("name")} data-testid="art-name" />
                        <input className={input} placeholder="Email *" type="email" value={form.email} onChange={set("email")} data-testid="art-email" />
                        <input className={input} placeholder="Phone" value={form.phone} onChange={set("phone")} data-testid="art-phone" />
                    </div>

                    <div className="flex flex-wrap items-center justify-between gap-4 border-t-2 border-zinc-900 pt-5">
                        <p className="font-mono text-[11px] uppercase tracking-[0.16em] text-zinc-600" data-testid="artwork-summary">
                            {service ? `you picked: ${(cfg?.tiers || []).find((t) => t.key === service)?.name}` : "pick a service above"}
                            {products.length ? ` · ${products.length} job${products.length > 1 ? "s" : ""}` : ""}
                        </p>
                        <StoreButton theme={theme} type="submit" disabled={busy} data-testid="art-submit">
                            {busy ? <Loader2 className="animate-spin" size={16} /> : <PencilRuler size={16} />} Send it to the art room
                        </StoreButton>
                    </div>
                </form>

                {cfg?.promise && (
                    <p className="mt-6 flex items-start gap-3 border-l-4 pl-4 text-base font-semibold text-zinc-900" style={{ borderColor: theme.primary }} data-testid="artwork-promise">
                        <ShieldCheck size={18} className="mt-0.5 shrink-0" style={{ color: theme.primary }} /> {cfg.promise}
                    </p>
                )}
            </section>

            <Fold title="What print-ready means for each job" kicker="What are you making?" testid="artwork-fold-jobs">
                <FoldBody>
                    <p className="max-w-2xl text-base text-zinc-600">Every job wants a different file. Tap a card to add that job to your request.</p>
                    <div className="mt-6"><JobCards jobs={jobs} theme={theme} picked={products} onPick={pick} /></div>
                </FoldBody>
            </Fold>
            <Fold title="The five things that ruin a print" kicker="Good file vs bad file" testid="artwork-fold-lessons" id="learn">
                <FoldBody>
                    <p className="max-w-2xl text-base text-zinc-600">Not a textbook. Look at the picture, read the one rule, send us better files.</p>
                    <div className="mt-6"><Lessons lessons={cfg?.lessons || []} theme={theme} /></div>
                </FoldBody>
            </Fold>
            <Fold title="File types, sizes, bleed and the cut" kicker="In normal English" testid="artwork-fold-files">
                <FoldBody>
                    <div className="grid gap-3 sm:grid-cols-2" data-testid="artwork-file-types">
                        {(cfg?.file_types || []).map((f) => (
                            <div key={f.ext} className="border-2 border-zinc-900 bg-white p-4">
                                <p className="flex items-baseline justify-between gap-3">
                                    <span className="font-mono text-sm font-black tracking-[0.1em]">{f.ext}</span>
                                    <span className={`font-mono text-[11px] uppercase tracking-[0.2em] ${VERDICT[f.verdict] || "text-zinc-500"}`}>{f.verdict}</span>
                                </p>
                                <p className="mt-2 text-sm text-zinc-700">{f.plain}</p>
                            </div>
                        ))}
                    </div>
                    <div className="mt-8 grid items-center gap-8 lg:grid-cols-2">
                        {cfg?.trim?.image && <img src={cfg.trim.image} alt="Banner trim marks" loading="lazy" className="w-full border-2 border-zinc-900 object-cover" />}
                        <div data-testid="artwork-trim">
                            <h3 className="font-display text-xl font-black uppercase tracking-tight">Sizes, bleed and the cut</h3>
                            <ul className="mt-4 space-y-2">
                                {(cfg?.trim?.rules || []).map((r) => (
                                    <li key={r} className="flex gap-3 text-base text-zinc-700"><span className="mt-2 h-1.5 w-1.5 shrink-0" style={{ background: theme.primary }} /> {r}</li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </FoldBody>
            </Fold>
        </Layout>
    );
}
